/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React, { useEffect, useRef } from 'react';
import { X, Printer, Loader2 } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { useAccessToken } from '@/stores/authStore';

interface PrintInvoiceDialogProps {
  /** Invoice ID to print */
  invoiceId: string;
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback when dialog is closed */
  onClose: () => void;
}

/**
 * Print Invoice Dialog Component
 *
 * Custom print dialog that loads the stealth variant (print-optimized)
 * and provides a print preview with manual print trigger.
 *
 * Features:
 * - Print-optimized template (stealth variant - minimal colors)
 * - Print preview before printing
 * - Custom print button (not native browser print)
 * - Keyboard support (ESC to close, Ctrl+P to print)
 * - Loading state
 */
export function PrintInvoiceDialog({
  invoiceId,
  isOpen,
  onClose,
}: PrintInvoiceDialogProps) {
  const { t, i18n } = useTranslation('core');
  const accessToken = useAccessToken();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [html, setHtml] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Always use hybrid variant for printing (red on white, print-friendly)
  const variant = 'hybrid';
  const language = i18n.language.startsWith('fr') ? 'fr' : 'en';

  // Fetch invoice HTML when dialog opens
  useEffect(() => {
    if (!isOpen || !invoiceId) {
      return;
    }

    const fetchInvoiceHtml = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `/api/v1/billing/invoices/${invoiceId}/html/${variant}`,
          {
            headers: {
              'Accept-Language': language,
              ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch invoice: ${response.statusText}`);
        }

        const htmlContent = await response.text();
        setHtml(htmlContent);
      } catch (err) {
        console.error('Error fetching invoice HTML:', err);
        setError(err instanceof Error ? err.message : 'Failed to load invoice');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoiceHtml();
  }, [isOpen, invoiceId, variant, language]);

  // Update iframe content when HTML changes
  useEffect(() => {
    if (html && iframeRef.current) {
      const iframeDoc = iframeRef.current.contentDocument;
      if (iframeDoc) {
        iframeDoc.open();
        iframeDoc.write(html);
        iframeDoc.close();
      }
    }
  }, [html]);

  // Handle print
  const handlePrint = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus();
      iframeRef.current.contentWindow.print();
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      // ESC to close
      if (e.key === 'Escape') {
        onClose();
      }

      // Ctrl+P or Cmd+P to print
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        handlePrint();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        {/* Backdrop */}
        <Dialog.Overlay className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />

        {/* Dialog Content */}
        <Dialog.Content className="fixed inset-0 z-50 flex flex-col items-center justify-center p-4 sm:p-6 md:p-8">
          <div className="relative w-full max-w-7xl h-full max-h-[95vh] bg-surface border border-border-subtle rounded-2xl shadow-2xl flex flex-col overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle bg-background/50 backdrop-blur-sm">
              <Dialog.Title className="text-lg font-semibold text-foreground">
                {t('invoices.printPreview')}
              </Dialog.Title>

              <div className="flex items-center gap-2">
                {/* Print Button */}
                <button
                  onClick={handlePrint}
                  disabled={isLoading || !!error}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Printer className="w-4 h-4" />
                  <span>{t('common.print')}</span>
                </button>

                {/* Close Button */}
                <Dialog.Close asChild>
                  <button
                    className="inline-flex items-center justify-center p-2 text-muted hover:text-foreground hover:bg-hover rounded-lg transition-colors"
                    aria-label={t('common.close')}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </Dialog.Close>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden bg-background/30">
              {isLoading && (
                <div className="flex flex-col items-center justify-center h-full gap-3">
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                  <p className="text-sm text-muted">{t('invoices.loadingPrintPreview')}</p>
                </div>
              )}

              {error && (
                <div className="flex flex-col items-center justify-center h-full gap-3">
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-sm text-red-500">{error}</p>
                  </div>
                  <button
                    onClick={() => {
                      setHtml(null);
                      setError(null);
                      setIsLoading(true);
                    }}
                    className="px-4 py-2 text-sm font-medium text-foreground bg-background hover:bg-hover border border-border-subtle rounded-lg transition-colors"
                  >
                    {t('common.retry')}
                  </button>
                </div>
              )}

              {!isLoading && !error && html && (
                <iframe
                  ref={iframeRef}
                  className="w-full h-full border-0"
                  title={t('invoices.printPreview')}
                  sandbox="allow-same-origin allow-modals"
                />
              )}
            </div>

            {/* Footer hint */}
            <div className="px-6 py-3 bg-background/50 backdrop-blur-sm border-t border-border-subtle">
              <p className="text-xs text-muted text-center">
                {t('common.pressEscToClose')} • {t('common.pressCtrlPToPrint')}
              </p>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
